import classNames from "classnames";

import type { SortDirection } from "types/types";

// We don't want callers to have a dependency on GraphQL, so we re-export the
// type here.
export type SortOrder = SortDirection;

export type CoreProps<T> = {
  columns: readonly GridColumn<T, keyof T>[];
  data: T[];
  cellRenderer?: React.ComponentType<
    CellData<T, keyof T> & {
      row: number;
      column: number;
      title?: string;
      className: string;
      children: React.ReactNode;
    }
  >;
  className?: string;
  noRowsText?: React.ReactNode;
  pageSize?: number;
  striped?: boolean;
  style?: React.CSSProperties;
};

export type CommonSortProps<T> = {
  columns: readonly SortColumn<T, keyof T>[];
};

export type CellData<T, F extends keyof T> = {
  field: F;
  // TODO: using recursive conditional types, it should be possible
  // to define a stricter type for fieldData here; however, naively
  // typing this as T[F] ends up with fieldData as the _union_ of
  // possible column types, which is not usable.
  // See https://github.com/microsoft/TypeScript/pull/40002
  fieldData: any;
  rowData: T;
};

export type GridColumnClassName<T, F extends keyof T> =
  | string
  | ((args: CellData<T, F>) => string);

type StyledGridColumn<T, F extends keyof T> = {
  style?: "number" | "query";
  className?: GridColumnClassName<T, F>;
  headerClassName?: string;
};

export type Field<T> = keyof T & string;

export type GridColumn<T, F extends keyof T> = {
  header?: React.ReactNode;
  tip?: React.ReactNode;
  field: Field<T>;
  title?: boolean | ((args: CellData<T, F>) => string);
  // We are not typing this as React.ComponentType<CellData<T, F>> (which seems
  // like a more sensible definition) due to the issues discussed here:
  // https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/53846
  renderer?: (props: CellData<T, F>) => React.ReactNode;
  /**
   * Used to render null or undefined nodes instead of calling the renderer.
   * If not set, the renderer will be called for null or undefined values.
   */
  nullValue?: React.ReactNode;
  disableSort?: boolean;
  defaultSortOrder?: SortOrder;
  /**
   * Width of the column, as a single track of the
   * {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns grid-template-columns}
   * CSS property (e.g., "1fr" or "minmax(10px,20%)").
   *
   * Can alternately be set to 'false' to omit this column from the grid
   * entirely.
   */
  width: string | false;
} & StyledGridColumn<T, F>;

export type SortColumn<T, F extends keyof T> = Pick<
  GridColumn<T, F>,
  "field" | "defaultSortOrder"
>;

export function getClassName<T, F extends keyof T>(
  f: StyledGridColumn<T, F>,
  field: F,
  fieldData: T[F],
  rowData: T,
): string | undefined {
  const className =
    typeof f.className === "function"
      ? f.className({ field: field, fieldData: fieldData, rowData: rowData })
      : f.className;
  const builtInStyling =
    f.style === "number"
      ? "text-right font-mono"
      : f.style === "query"
      ? "font-mono"
      : undefined;
  return classNames(builtInStyling, className);
}

export function getHeaderClassName<T, F extends keyof T>(
  f: StyledGridColumn<T, F>,
): string | undefined {
  const builtInStyling =
    f.style === "number"
      ? "!text-right"
      : // queries have no special header style
        undefined;

  return classNames(builtInStyling, f.headerClassName);
}

export type GridSortStateEntry = {
  columnIdx: number;
  order: SortOrder;
};

export type GridColumnSortState = GridSortStateEntry[];
export type GridColumnSortedSortState = [
  GridSortStateEntry,
  ...GridSortStateEntry[],
];

export type GridSortState<T> = {
  data: T[];
  entries: GridSortStateEntry[];
  doSort: (sortColumnIndex: number) => void;
};

export function reverse(order: SortOrder): SortOrder {
  switch (order) {
    case "ASC":
      return "DESC";
    case "DESC":
      return "ASC";
  }
}

export function updateSortState<T>(
  current: GridColumnSortState,
  columns: readonly SortColumn<T, keyof T>[],
  sortByIdx: number,
): GridColumnSortedSortState {
  const sortColumn = columns[sortByIdx];
  const prevLeadSort = current.length > 0 ? current[0] : undefined;

  // If the last sort was by this column, reverse that order. Otherwise,
  // use the column's default sort order or fall back to an ascending sort.
  const newSortOrder =
    prevLeadSort?.columnIdx === sortByIdx
      ? reverse(prevLeadSort.order)
      : sortColumn.defaultSortOrder ?? "ASC";

  // we want to include previous columns to effectively achieve a stable sort
  const otherEntries = current.filter((entry) => entry.columnIdx !== sortByIdx);

  return [{ columnIdx: sortByIdx, order: newSortOrder }, ...otherEntries];
}
